<script setup lang="ts">
import type { ModelKey } from '~/features/my-wallet/constants/my-wallet'
import type { RequirementView } from '~/features/my-wallet/types/receiving-model'
import { I18nT, useI18n } from 'vue-i18n'
import IconCheck from '~icons/material-symbols/check'
import IconClose from '~icons/material-symbols/close'
import { PERFORMANCE_WINDOW_DAYS } from '~/features/my-wallet/constants/my-wallet'

interface Props {
  requirement: RequirementView
  model: ModelKey
}

const props = defineProps<Props>()

const { t } = useI18n()

const SINGLE_HIGHLIGHT_CODES = ['REGISTRATION_ENABLED_FOR_FINANCING', 'MIN_COMPLETED_INSTALLATIONS', 'DELINQUENCY_UNDER_CONTROL']
const ON_TIME_RANGE_CODE = 'ON_TIME_PERCENTAGE_RANGE'
const MODELS_WITH_ON_TIME_RANGE: ModelKey[] = ['B', 'A']

const requirementKeyPath = computed(() => `my_wallet.receiving_model.card.requirements.${props.requirement.code}`)
const hasSingleHighlight = computed(() => SINGLE_HIGHLIGHT_CODES.includes(props.requirement.code))
const isOnTimeRange = computed(() => props.requirement.code === ON_TIME_RANGE_CODE && MODELS_WITH_ON_TIME_RANGE.includes(props.model))
const statusLabel = computed(() => t(`my_wallet.receiving_model.card.requirement_status.${props.requirement.marker}`))
</script>

<template>
  <li class="model-requirement" :class="`-${requirement.marker}`">
    <span class="model-requirement-marker" aria-hidden="true">
      <IconCheck v-if="requirement.marker === 'done'" class="model-requirement-marker-icon" />
      <IconClose v-else-if="requirement.marker === 'alert'" class="model-requirement-marker-icon" />
    </span>
    <span class="sr-only">{{ t('my_wallet.receiving_model.card.requirement_status_label', { status: statusLabel }) }}</span>

    <div v-if="isOnTimeRange" class="model-requirement-text -range" :class="{ '-waived': requirement.isWaived }">
      <p>{{ t(`${requirementKeyPath}.label`, { days: PERFORMANCE_WINDOW_DAYS }) }}</p>
      <I18nT :keypath="`${requirementKeyPath}.text`" tag="p">
        <template #range>
          <strong>{{ t(`${requirementKeyPath}.ranges.${model}`) }}</strong>
        </template>
        <template #onTime>
          <strong>{{ t(`${requirementKeyPath}.on_time`) }}</strong>
        </template>
      </I18nT>
    </div>

    <I18nT
      v-else-if="hasSingleHighlight"
      :keypath="`${requirementKeyPath}.text`"
      tag="p"
      class="model-requirement-text"
      :class="{ '-waived': requirement.isWaived }"
    >
      <template #highlight>
        <strong :class="{ 'model-requirement-underline': requirement.code === 'DELINQUENCY_UNDER_CONTROL' }">
          {{ t(`${requirementKeyPath}.highlight`) }}
        </strong>
      </template>
    </I18nT>

    <p v-else class="model-requirement-text" :class="{ '-waived': requirement.isWaived }">
      {{ t('my_wallet.receiving_model.card.requirements.fallback') }}
    </p>
  </li>
</template>

<style scoped lang="scss">
.model-requirement {
  @apply flex items-start gap-2 w-full;

  &.-waived {
    @apply opacity-50;
  }

  .model-requirement-marker {
    @apply flex items-center justify-center flex-shrink-0 w-4 h-4 mt-0.5 rounded-full;
  }

  &.-done .model-requirement-marker {
    @apply bg-feedback-positive-pure text-neutral-high-pure;
  }

  &.-alert .model-requirement-marker {
    @apply bg-feedback-negative-pure text-neutral-high-pure;
  }

  &.-waived .model-requirement-marker {
    @apply bg-brand-primary-light;
  }

  &.-pending .model-requirement-marker {
    @apply border-brand-primary-light;
    border-width: 1.5px;
  }

  .model-requirement-marker-icon {
    @apply w-[11px] h-[11px];
  }

  .model-requirement-text {
    @apply flex-1 min-w-0 fonts-body-large-regular text-neutral-low-dark;
    line-height: 1.375;

    &.-range {
      @apply flex flex-col gap-1;
      line-height: 1.5;
    }

    &.-waived {
      @apply line-through;
    }
  }

  .model-requirement-underline {
    @apply underline;
  }
}
</style>
